import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { SeparationConfig } from "@capture/shared";

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface HotFolderConfig {
  id: string;
  tenantId: string;
  name: string;
  watchPath: string;
  jobTypeId: string;
  pollingIntervalSec: number;
  completenessMode: string;
  sentinelFilename: string | null;
  idleTimeoutSec: number | null;
  isEnabled: boolean;
  // R-266 6a — when true, files dropped in this folder create a typeless batch
  // routed by document fingerprint rather than the configured job type.
  routeByContent: boolean;
  // R-67 S1 — manifest parsing: when manifestMode !== "none", a manifest file
  // (manifestFilename, default manifest.xml) is run through the header pipeline.
  manifestMode: string;
  manifestFilename: string | null;
  // Slice 3a — per-channel separation override. Null means "use job type config".
  separationOverride: SeparationConfig | null;
  duplexOverride: boolean | null;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════
// Hook
// ═══════════════════════════════════════════════════════════════

export function useHotFolders() {
  const [hotFolders, setHotFolders] = useState<HotFolderConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<{ configs: HotFolderConfig[] }>("/hot-folders");
      setHotFolders(data.configs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (data: Omit<HotFolderConfig, "id" | "tenantId" | "createdAt" | "updatedAt">): Promise<HotFolderConfig> => {
    const result = await api.post<HotFolderConfig>("/hot-folders", data);
    await load();
    return result;
  };

  const update = async (id: string, data: Partial<Omit<HotFolderConfig, "id" | "tenantId" | "createdAt" | "updatedAt">>): Promise<HotFolderConfig> => {
    const result = await api.put<HotFolderConfig>(`/hot-folders/${id}`, data);
    await load();
    return result;
  };

  const remove = async (id: string): Promise<void> => {
    await api.delete(`/hot-folders/${id}`);
    await load();
  };

  const toggle = async (id: string): Promise<HotFolderConfig> => {
    const result = await api.post<HotFolderConfig>(`/hot-folders/${id}/toggle`, {});
    await load();
    return result;
  };

  return { hotFolders, isLoading, error, refresh: load, create, update, remove, toggle };
}
